import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';

import { VolumeEmptyActionPanel } from '../../components/VolumeEmptyActionPanel';
import { useDiskPricingSync } from '../../components/diskPricingSync';
import { isReclaimPolicyLocked, resolveReclaimPolicyValue } from '../../components/reclaimPolicyField';
import { buildStorageClassLabelMap, useStorageClassFormValidation } from '../../components/storageClassField';
import { VendorDevicePersistFields } from '../../components/vendorDevice';
import { VolumeResourceCard, buildDiskVolumeExtraItems } from '../../components/volumeResourceCard';
import type { CapabilityControlProps } from '../../core/types';
import { isActionVisible, isFieldVisible, resolveActionMode, resolveFieldMode } from '../../platform/capability-registry/resolveFieldMode';
import { shouldHideEmptyListBlock } from '../../platform/capability-registry/shouldHideEmptyListBlock';
import { mergeOptionMetaForValues } from '../../platform/option-meta';
import { StorageDataDiskCreateModal, getStorageDataDiskCreateModalValues } from './StorageDataDiskCreateModal';
import { StorageDataDiskSelectModal, getStorageDataDiskSelectModalValues } from './StorageDataDiskSelectModal';
import { STORAGE_ACTION_KEYS, STORAGE_FIELD_KEYS } from './fieldKeys';
import styles from './index.module.less';
import type { DataDiskRecord, StorageBlockProps } from './types';
import {
  buildDataDiskFromCreateForm,
  dataDiskIndexesMissingStorageClass,
  diskToCardItem,
  mergeEditedDataDisk,
  normalizeDataDiskList,
} from './utils';

const FormItem = Form.Item;

type ModalMode = 'create' | 'edit' | 'select' | null;

function isReadonlyBlock(control: CapabilityControlProps): boolean {
  return control.blockMode === 'preview';
}

function isReadonlyStorageList(control: CapabilityControlProps): boolean {
  const mode = resolveFieldMode(STORAGE_FIELD_KEYS.list, control);
  return mode === 'readonly' || isReadonlyBlock(control);
}

function isActionEnabled(actionKey: string, control: CapabilityControlProps): boolean {
  return resolveActionMode(actionKey, control) === 'enabled';
}

function readDiskList(form: StorageBlockProps['form'], path: string): DataDiskRecord[] {
  return normalizeDataDiskList(form.getFieldValue(path));
}

function writeDiskList(form: StorageBlockProps['form'], path: string, list: DataDiskRecord[]) {
  form.setFieldValue(path, list);
}

/** 数据盘能力块：列表 + 新建空白盘 / 选择已有云盘 */
export function StorageBlock(props: StorageBlockProps) {
  const { form, paths, options, linkageProps, ...control } = props;
  const { t } = useTranslation();
  const [createForm] = Form.useForm();
  const [selectForm] = Form.useForm();
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { pricingActive, syncListPricing } = useDiskPricingSync(options);

  const readonly = isReadonlyStorageList(control);

  const namespace = linkageProps?.namespace ?? options?.namespace;
  const disksRaw = Form.useWatch(paths.disks, form);
  const optionMeta = Form.useWatch('optionMeta', form);
  const diskList = useMemo(() => normalizeDataDiskList(disksRaw), [disksRaw]);

  const { rules: storageClassRules, missingIndexes: storageClassErrorIndexes } = useStorageClassFormValidation(
    disksRaw,
    pricingActive,
    dataDiskIndexesMissingStorageClass,
    t('common:please.select.storage.class'),
  );

  const unconfiguredLabel = t('data-center:maas.storage.not.configured');

  const storageClassLabelMap = useMemo(() => {
    const formValues = form.getFieldsValue() as Record<string, unknown>;
    return buildStorageClassLabelMap(diskList, { ...formValues, optionMeta }, paths.disks);
  }, [diskList, form, optionMeta, paths.disks]);

  const closeModal = useCallback(() => {
    setModalMode(null);
    setEditingIndex(null);
    createForm.resetFields();
    selectForm.resetFields();
  }, [createForm, selectForm]);

  const openCreate = useCallback(() => {
    setModalMode('create');
    setEditingIndex(null);
    createForm.resetFields();
  }, [createForm]);

  const openEdit = useCallback(
    (index: number) => {
      setModalMode('edit');
      setEditingIndex(index);
      createForm.resetFields();
    },
    [createForm],
  );

  const openSelect = useCallback(() => {
    setModalMode('select');
    setEditingIndex(null);
    selectForm.resetFields();
  }, [selectForm]);

  const handleRemove = useCallback(
    (index: number) => {
      const nextList = readDiskList(form, paths.disks).filter((_, i) => i !== index);
      writeDiskList(form, paths.disks, nextList);
      syncListPricing(form, paths.disks, nextList);
    },
    [form, paths.disks, syncListPricing],
  );

  const handleCreateOk = useCallback(async () => {
    const values = getStorageDataDiskCreateModalValues(createForm, { resourceEditMode: options?.resourceEditMode });
    if (!values.diskName) {
      return Promise.reject();
    }
    const nextDisk = buildDataDiskFromCreateForm(values);
    const list = readDiskList(form, paths.disks);
    const initDisk = editingIndex != null ? list[editingIndex] : undefined;

    const record = modalMode === 'edit' && editingIndex != null ? mergeEditedDataDisk(initDisk, nextDisk) : nextDisk;

    const nextList =
      modalMode === 'create'
        ? [...list, record]
        : modalMode === 'edit' && editingIndex != null
          ? list.map((item, index) => (index === editingIndex ? record : item))
          : list;

    writeDiskList(form, paths.disks, nextList);
    syncListPricing(form, paths.disks, nextList);
    const modalValues = createForm.getFieldsValue() as Record<string, unknown>;
    mergeOptionMetaForValues(form, modalValues, [record.storageClass, record.reclaimPolicy]);
    closeModal();
  }, [closeModal, createForm, editingIndex, form, modalMode, options?.resourceEditMode, paths.disks, syncListPricing]);

  const handleSelectOk = useCallback(async () => {
    const selectedDisk = getStorageDataDiskSelectModalValues(selectForm);
    if (!selectedDisk) {
      return Promise.reject();
    }
    const reclaimLocked = isReclaimPolicyLocked(options?.resourceEditMode);
    const diskWithReclaim = {
      ...selectedDisk,
      reclaimPolicy: resolveReclaimPolicyValue(selectedDisk.reclaimPolicy, reclaimLocked),
    };
    const list = readDiskList(form, paths.disks);
    writeDiskList(form, paths.disks, [...list, diskWithReclaim]);
    mergeOptionMetaForValues(form, selectForm.getFieldsValue() as Record<string, unknown>, [
      diskWithReclaim.storageClass,
      diskWithReclaim.reclaimPolicy,
    ]);
    options?.onResourceWatchSnapshot?.(form);
    closeModal();
  }, [closeModal, form, options, paths.disks, selectForm]);

  const canAdd = isActionVisible(STORAGE_ACTION_KEYS.add, control) && !readonly;
  const canSelect = isActionVisible(STORAGE_ACTION_KEYS.select, control) && !readonly;
  const canEditAction = isActionVisible(STORAGE_ACTION_KEYS.edit, control) && !readonly;
  const canRemoveAction = isActionVisible(STORAGE_ACTION_KEYS.remove, control) && !readonly;

  const editingDisk = editingIndex != null ? diskList[editingIndex] : undefined;

  if (!isFieldVisible(STORAGE_FIELD_KEYS.list, control)) {
    return null;
  }

  if (shouldHideEmptyListBlock(control, STORAGE_FIELD_KEYS.list, diskList.length === 0)) {
    return null;
  }

  return (
    <>
      <FormItem field={paths.disks} hidden rules={storageClassRules} style={{ display: 'none', margin: 0 }}>
        <Input />
      </FormItem>
      {pricingActive
        ? diskList.map((disk, index) => (
            <VendorDevicePersistFields key={`${disk.diskName}-${index}-pricing`} fieldPrefix={`${paths.disks}.${index}.pricing`} />
          ))
        : null}

      {diskList.length > 0 ? (
        <div className={styles.list}>
          {diskList.map((disk, index) => {
            const cardItem = diskToCardItem(disk, storageClassLabelMap);
            return (
              <VolumeResourceCard
                key={`${cardItem.diskName}-${index}`}
                value={cardItem.diskName}
                label={cardItem.diskNickname || cardItem.diskName}
                extraItems={buildDiskVolumeExtraItems(cardItem, t, {
                  emptyStorageClassFallback: !pricingActive ? unconfiguredLabel : undefined,
                })}
                error={storageClassErrorIndexes.has(index)}
                canEdit={canEditAction && isActionEnabled(STORAGE_ACTION_KEYS.edit, control)}
                canDelete={canRemoveAction && isActionEnabled(STORAGE_ACTION_KEYS.remove, control) && !cardItem.locked}
                onEdit={() => openEdit(index)}
                onDelete={() => handleRemove(index)}
              />
            );
          })}
        </div>
      ) : null}

      {!readonly ? (
        <VolumeEmptyActionPanel
          items={[
            ...(canAdd && isActionEnabled(STORAGE_ACTION_KEYS.add, control)
              ? [{ key: 'create', title: t('components:create.data.disk'), onClick: openCreate }]
              : []),
            ...(canSelect && isActionEnabled(STORAGE_ACTION_KEYS.select, control)
              ? [{ key: 'select', title: t('components:add.existing.data.disk'), onClick: openSelect }]
              : []),
          ]}
        />
      ) : null}

      <StorageDataDiskCreateModal
        visible={modalMode === 'create' || modalMode === 'edit'}
        form={createForm}
        initDisk={modalMode === 'edit' ? editingDisk : undefined}
        billModelId={options?.billModelId}
        deploymentTypeId={options?.deploymentTypeId}
        paymentTiming={options?.paymentTiming}
        skuType={options?.skuType}
        resourceEditMode={options?.resourceEditMode}
        resourceCode={options?.resourceCode}
        onOk={handleCreateOk}
        onCancel={closeModal}
      />

      <StorageDataDiskSelectModal
        visible={modalMode === 'select'}
        form={selectForm}
        namespace={namespace}
        onOk={handleSelectOk}
        onCancel={closeModal}
      />
    </>
  );
}
